// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCompareChangesColumn.test.tsx
import {
  type AskPunttReview,
  type AskPunttReviewResolved,
} from "@mg/schemas/src/christo/askPunttReview";
import { type AskPunttComparisonRegion } from "@mg/schemas/src/christo/versionComparison";
import { useMemo } from "react";

import { AskV2ReviewCompareChanges } from "./AskV2ReviewCompareChanges";
import {
  useReopenAskPunttReviewResolved,
  useSetAskPunttReviewChangeReviewed,
} from "./useAskPunttReview";
import { type useAskV2ReviewVersionCompare } from "./useAskV2ReviewVersionCompare";

import { type useReviewArtworkFocus } from "../../components/ask-puntt-review";

type AskV2ReviewCompareChangesColumnProps = {
  review: AskPunttReview;
  compare: ReturnType<typeof useAskV2ReviewVersionCompare>;
  regions: readonly AskPunttComparisonRegion[];
  artworkPaneRef: ReturnType<typeof useReviewArtworkFocus>["artworkPaneRef"];
  readOnly: boolean;
  /** Leaves the comparison to show a fixed comment in the review. */
  onViewFix: (commentId: string) => void;
};

/**
 * The left column while a comparison shows its changes: the changes list,
 * wired to the reviewed marks and to the comments each change fixed.
 */
export function AskV2ReviewCompareChangesColumn({
  review,
  compare,
  regions,
  artworkPaneRef,
  readOnly,
  onViewFix,
}: AskV2ReviewCompareChangesColumnProps) {
  const { changeAt, comparison, newerReview } = compare;
  const setChangeReviewed = useSetAskPunttReviewChangeReviewed();
  const reopenResolved = useReopenAskPunttReviewResolved();

  // The older version's comments each changed window fixed, off the newer
  // review's resolved list (a window number is a change number).
  const olderReviewId =
    newerReview == null
      ? null
      : newerReview.reviewId === review.reviewId
        ? compare.compareReviewId
        : review.reviewId;
  const fixesByWindow = useMemo(() => {
    const byWindow = new Map<number, AskPunttReviewResolved[]>();
    for (const entry of newerReview?.resolved ?? []) {
      if (entry.reopenedAt || entry.priorReviewId !== olderReviewId) continue;
      for (const window of entry.windows) {
        byWindow.set(window, [...(byWindow.get(window) ?? []), entry]);
      }
    }
    return byWindow;
  }, [newerReview?.resolved, olderReviewId]);

  return (
    // why: the column follows the pins. With the comparison showing
    // its changes, a list of findings beside numbered windows read
    // as if finding 3 were change 3 (story: the compare screen).
    <AskV2ReviewCompareChanges
      regions={regions}
      selectedIndex={changeAt}
      onSelect={compare.setChangeAt}
      // The open viewer's page; the linked compared one follows it.
      onLocate={(region) => artworkPaneRef.current?.navigateToPage(region.page)}
      beforeMark={compare.compareSideMark("before")}
      afterMark={compare.compareSideMark("after")}
      reviewed={newerReview ? compare.reviewedNumbers : undefined}
      fixes={fixesByWindow}
      onReopenFix={
        readOnly || !newerReview
          ? undefined
          : (commentId) =>
              reopenResolved.mutate({
                reviewId: newerReview.reviewId,
                commentId,
                reopened: true,
              })
      }
      reopenPending={reopenResolved.isPending}
      // why only with the newer version open: its Previously raised list is
      // where a fixed comment is shown, and only the open review's list is on
      // screen.
      onViewFix={
        newerReview?.reviewId === review.reviewId ? onViewFix : undefined
      }
      onToggleReviewed={
        newerReview && comparison
          ? (number, reviewed) =>
              setChangeReviewed.mutate({
                reviewId: newerReview.reviewId,
                comparisonId: comparison.id,
                number,
                reviewed,
              })
          : undefined
      }
    />
  );
}
